import React from 'react';

export interface PageMeta {
  title: string;
  description: string;
  /** Absolute URL of the share image (1200x630). */
  image: string;
  /** Canonical absolute URL of the page. */
  url: string;
  type?: 'website' | 'article';
}

const SITE_TITLE = 'Rescue Ally';

/**
 * Per-page <title> and social meta. public/index.html carries the site-wide
 * defaults; this overwrites them in the document head so the prerenderer
 * (puppeteer, `page.content()` after network idle) bakes the post's own
 * title, description and og:image into its static HTML. That is what link
 * previews on Instagram, Facebook and ad platforms read.
 *
 * Runs in a layout effect so it lands before paint and before prerender
 * capture. Restores the defaults on unmount so client-side navigation back
 * to the home page does not keep a post's title.
 */
export const usePageMeta = (meta: PageMeta): void => {
  React.useLayoutEffect(
    function applyPageMeta() {
      const previousTitle = document.title;
      const previous = new Map<string, string | null>();

      const upsert = (attribute: 'name' | 'property', key: string, value: string) => {
        const selector = `meta[${attribute}="${key}"]`;
        let element = document.head.querySelector<HTMLMetaElement>(selector);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute(attribute, key);
          document.head.appendChild(element);
        }
        previous.set(selector, element.getAttribute('content'));
        element.setAttribute('content', value);
      };

      const fullTitle = `${meta.title} | ${SITE_TITLE}`;
      document.title = fullTitle;
      upsert('name', 'description', meta.description);
      upsert('property', 'og:title', meta.title);
      upsert('property', 'og:description', meta.description);
      upsert('property', 'og:type', meta.type ?? 'article');
      upsert('property', 'og:image', meta.image);
      upsert('property', 'og:url', meta.url);
      upsert('name', 'twitter:title', meta.title);
      upsert('name', 'twitter:description', meta.description);
      upsert('property', 'twitter:image', meta.image);

      return () => {
        document.title = previousTitle;
        for (const [selector, content] of previous) {
          const element = document.head.querySelector<HTMLMetaElement>(selector);
          if (!element) continue;
          if (content === null) element.remove();
          else element.setAttribute('content', content);
        }
      };
    },
    [meta.title, meta.description, meta.image, meta.url, meta.type]
  );
};
